// Committed units on the full unit page (docs/decisions/inventory-columns.md,
// decision 7): units marked sold but not yet closed (sale pending, pre-sold,
// sold), loaded from GET /api/inventory/committed. They open the same page as
// a unit in stock, but nothing that only makes sense for a unit still for sale
// is shown for them: no sell-speed risk, days-to-sell forecast, carrying cost,
// anomaly signal or "live quote ≥ AI price" opportunity.
import { dealStatusView } from './dealStatus';

export const NOT_FOR_COMMITTED = 'Not available for committed units';

// The payload's in-stock-only fields: blanked for a committed unit, so every
// card that reads them stays hidden whatever the endpoint sends.
export const IN_STOCK_ONLY_FIELDS = Object.freeze([
    // sell-speed risk and the days-to-sell forecast
    'RiskTier', 'LadenhueterRisk', 'PredictedBucket', 'BucketConfidence',
    'DaysToSellPrediction', 'DaysToSellCategory',
    'MedianDays', 'RangeLowDays', 'RangeHighDays', 'UpperDays',
    'PSoldBy30', 'PSoldBy60', 'PSoldBy90', 'PSoldBy180', 'ExpectedSaleDate', 'SurvivalCurve',
    'PredictedSaleDate',
    // carrying cost (the deal is done; for a unit not yet received it never started)
    'CarryingCostDaily', 'CarryingCostToDate', 'CarryingCostProjected', 'CarryingCostTotalExposure',
    'MarginAfterCarrying', 'MarginAfterCarryingPct', 'MarginAtSale', 'MarginAtSalePct',
    // signals and opportunities
    'AnomalyVerdict', 'AnomalySeverity', 'AnomalyReason', 'HasQualifyingQuote',
    // The backend's aging bucket (QA P2-1): the committed list is kept for a
    // while and never enters the counts, so its bucket is always computed
    // from DaysInStock with the settings in force now, never a stale id.
    'DaysInStockBucketId', 'DaysInStockBucket',
]);

// Set on every cleaned committed unit, so the grid, the preview and sorting
// can tell it from a unit in stock whatever else it carries.
export const COMMITTED_FLAG = 'IsCommitted';

/**
 * The unit as the app shows it when it is committed: in-stock-only fields
 * blanked and `IsCommitted` set. Idempotent.
 */
export const committedUnitView = (unit) => {
    const out = { ...unit };
    IN_STOCK_ONLY_FIELDS.forEach((f) => { out[f] = null; });
    out[COMMITTED_FLAG] = true;
    return out;
};

/** True for a unit cleaned by committedUnitView. */
export const isCommittedUnit = (unit) => Boolean(unit && unit[COMMITTED_FLAG]);

/**
 * The /api/inventory/committed payload as the app keeps it (QA Re-test 2,
 * R2-B1): every unit cleaned once, where the payload is loaded
 * (DataContext.loadCommitted), so the grid, the preview, the unit page and
 * sorting all see the same unit.
 */
export const committedPayload = (data) => {
    const units = Array.isArray(data?.units) ? data.units.map(committedUnitView) : [];
    return { units, count: Number.isFinite(data?.count) ? data.count : units.length };
};

// The statuses the unit page names in its hero and Overview: the committed
// ones, and On hold (a unit in stock that Salesforce holds; decision 6 as
// changed 16 Oct 2026). Quoted and Available keep today's page.
const HEADLINE_STATUSES = Object.freeze(['sale_pending', 'pre_sold', 'sold', 'on_hold']);

/**
 * The hero's deal line, or null: "Sale pending · marked sold Sep 22",
 * "Sold · invoiced Dec 3, 2025" (the year only when it is not this year),
 * "On hold". `tip` explains the status in words (for On hold: the reason);
 * `reason` is the hold reason, if any.
 */
export const dealHeadline = (unit, today = new Date()) => {
    const view = dealStatusView(unit, today);
    if (!view || !HEADLINE_STATUSES.includes(view.status)) return null;
    return {
        status: view.status,
        label: view.label,
        text: view.dateText ? `${view.label} · ${view.dateText}` : view.label,
        tip: view.tip,
        reason: view.reason,
    };
};
